import { PANEL_IDS } from '../../config/panelRegistry';

/**
 * Столбцы таблицы «Жалобы за период» и то, что от них зависит.
 *
 * Видимость столбца — та же настройка «Интерфейса», что у панелей: id столбца лежит в реестре
 * (`group: 'column'`), и порядок здесь обязан совпадать с порядком реестра — экран настроек
 * перечисляет столбцы в порядке реестра. Столбца «Подробнее» в списке нет: в нём кнопка
 * «Разобрать», скрывать его нельзя.
 *
 * - `requiresIiko` — точка кейса берётся из заказа iiko, без интеграции столбца нет при любой галке;
 * - `filter` — фильтр над таблицей, который следует за видимостью столбца. Скрытый столбец
 *   уносит свой фильтр, как у графиков нод: фильтровать по тому, чего не видно, незачем.
 */
export const CASE_COLUMNS = [
  { panelId: PANEL_IDS['ui:complaints.col-date'] },
  { panelId: PANEL_IDS['ui:complaints.col-client'] },
  { panelId: PANEL_IDS['ui:complaints.col-calls'] },
  { panelId: PANEL_IDS['ui:complaints.col-point'], requiresIiko: true },
  { panelId: PANEL_IDS['ui:complaints.col-type'], filter: 'type' },
  { panelId: PANEL_IDS['ui:complaints.col-detail'], filter: 'detail' },
  { panelId: PANEL_IDS['ui:complaints.col-compensation'] },
  { panelId: PANEL_IDS['ui:complaints.col-status'] }
];

/** Идентификаторы видимых столбцов в порядке таблицы. */
export const visibleCaseColumns = ({ isVisible, iikoEnabled }) =>
  CASE_COLUMNS
    .filter((column) => (!column.requiresIiko || iikoEnabled) && isVisible(column.panelId))
    .map((column) => column.panelId);

/** Ширина строки-заглушки («Загрузка…», «Нет жалоб»): видимые столбцы плюс «Подробнее». */
export const caseColumnCount = (visibleColumns) => visibleColumns.length + 1;

/** Какие фильтры над таблицей показывать: только те, чей столбец виден. */
export const visibleCaseFilters = ({ isVisible }) => {
  const filters = { type: false, detail: false };

  for (const column of CASE_COLUMNS) {
    if (column.filter) {
      filters[column.filter] = isVisible(column.panelId);
    }
  }

  return filters;
};

/**
 * Значение фильтра, которое уходит в запрос: скрытый фильтр — это «все».
 *
 * Выбранное значение скрытого фильтра не должно фильтровать список: на холодном кэше настроек
 * первая отрисовка идёт с умолчанием «показывать», и значение, выбранное до ответа сервера,
 * иначе продолжило бы действовать через фильтр, которого на экране уже нет. Значение, а не флаг
 * видимости, ещё и держит запрос стабильным: смена видимости при пустом фильтре не
 * перезапрашивает список.
 */
export const caseFilterValue = (value, visible) => (visible ? value : 'all');

/**
 * Параметры запроса списка, сводки, разреза по точкам и выгрузки.
 *
 * `callType` — направление из верхней панели, общей для разделов. Раньше раздел его не
 * получал, и «Входящие» над «Жалобами» ничего не меняли.
 */
export const buildComplaintsQuery = ({
  dateParams,
  searchPhone,
  statusFilter,
  typeFilter,
  detailFilter,
  pointFilter,
  onlyUnresolved,
  hideNotComplaint,
  iikoEnabled,
  callType
}) => ({
  ...dateParams,
  call_type: callType && callType !== 'all' ? callType : undefined,
  search: searchPhone || undefined,
  status: statusFilter !== 'all' ? statusFilter : undefined,
  complaint_type: typeFilter !== 'all' ? typeFilter : undefined,
  complaint_detail: detailFilter !== 'all' ? detailFilter : undefined,
  point: iikoEnabled && pointFilter !== 'all' ? pointFilter : undefined,
  only_unresolved: onlyUnresolved || undefined,
  hide_not_complaint: hideNotComplaint || undefined
});
